import { JSX, useEffect, useState } from 'react'
import { Navigate, Route } from 'react-router-dom'
import Auth from '@/container/auth'
import { RequireAuth } from './requireAuth'
import { Role } from '@/types/Role'
import { DaycareType } from '@/types/Daycare'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import AccountProfile from '@/container/account/profile'
import AccountChilds from '@/container/account/childs'
import AccountNotification from '@/container/account/notification'
import CreateDaycare from '@/container/createDaycare'
import Home from '@/container/home'
import Daycare from '@/container/daycare'
import EventsHandler from '@/container/events/eventsHandler'
import NewsHandler from '@/container/news/newsHandler'
import Settings from '@/container/settings'
import Families from '@/container/families'
import Groups from '@/container/group'
import PlanningHandler from '@/container/planning/planningHandler'
import Request from '@/container/request'
import StaffPage from '@/container/staff'
import ForgottenPassword from '@/container/forgottenPassword'
import SuggestPage from '@/container/suggest'
import DraftingPage from '@/container/drafting'
import TaskNotePage from '@/container/taskNote/taskNote.tsx'
import NewsDraftHandler from '@/container/news/newsDraftHandler.tsx'
import MealPage from '@/container/meal/meal.tsx'

interface RouteConfigArgs {
    frontendVersion: string
}

export function RouteConfig({ frontendVersion }: Readonly<RouteConfigArgs>): JSX.Element {
    const { t } = useTranslation()

    const { user, apiVersion } = useAuth()

    const [currentDaycareType, setCurrentDaycareType] = useState<DaycareType>(DaycareType.daycare)

    useEffect(() => {
        if (user?.currentDaycare) setCurrentDaycareType(user?.currentDaycare?.type)
    }, [user?.currentDaycare])

    const authTitle: string = `${t('signUp')}/${t('login')}`

    return (
        <Route>
            <Route
                path='/'
                element={
                    <>
                        <title>{authTitle}</title>
                        <Auth />
                    </>
                }
            />
            <Route path='/account'>
                <Route index element={<Navigate to='profile' replace />} />
                <Route
                    path='profile'
                    element={
                        <>
                            <title>{t('profile')}</title>
                            <AccountProfile />
                        </>
                    }
                />
                <Route
                    path='notifications'
                    element={
                        <>
                            <title>{t('notifications')}</title>
                            <AccountNotification />
                        </>
                    }
                />
                <Route
                    path='childs'
                    element={
                        <>
                            <title>{t('childs')}</title>
                            <AccountChilds />
                        </>
                    }
                />
                <Route path='*' element={<Navigate to='/' replace />} />
            </Route>
            <Route
                path='/createDaycare'
                element={
                    <RequireAuth roles={[Role.cald, Role.trustee, Role.staff]}>
                        <>
                            <title>{t('createDaycare')}</title>
                            <CreateDaycare />
                        </>
                    </RequireAuth>
                }
            />
            <Route
                path='/daycare'
                element={
                    <RequireAuth roles={[Role.cald, Role.trustee, Role.staff, Role.parent]}>
                        <>
                            <title>{t('structure')}</title>
                            <Daycare />
                        </>
                    </RequireAuth>
                }
            />
            <Route
                path='/events'
                element={
                    <RequireAuth roles={[Role.cald, Role.trustee, Role.staff, Role.parent]}>
                        <>
                            <title>{t('events')}</title>
                            <EventsHandler />
                        </>
                    </RequireAuth>
                }
            />
            <Route
                path='/adults'
                element={
                    <RequireAuth roles={[Role.cald, Role.trustee, Role.staff]}>
                        <>
                            <title>{currentDaycareType === DaycareType.rpe ? t('publicList') : t('list')}</title>
                            <Families />
                        </>
                    </RequireAuth>
                }
            />
            <Route
                path='/childs'
                element={
                    <RequireAuth roles={[Role.cald, Role.trustee, Role.staff]}>
                        <>
                            <title>{t('contracts')}</title>
                            <Families />
                        </>
                    </RequireAuth>
                }
            />
            <Route
                path='/forgottenPassword'
                element={
                    <>
                        <title>{t('forgottenPassword')}</title>
                        <ForgottenPassword />
                    </>
                }
            />
            <Route
                path='/group'
                element={
                    <RequireAuth roles={[Role.cald, Role.trustee]}>
                        <>
                            <title>{t('group')}</title>
                            <Groups />
                        </>
                    </RequireAuth>
                }
            />
            <Route
                path='/home'
                element={
                    <RequireAuth roles={[Role.trustee, Role.staff, Role.parent, Role.cald]}>
                        <>
                            <title>{t('myAccount')}</title>
                            <Home backendVersion={apiVersion} frontendVersion={frontendVersion} />
                        </>
                    </RequireAuth>
                }
            />
            <Route
                path='/news'
                element={
                    <RequireAuth roles={[Role.cald, Role.trustee, Role.staff, Role.parent]}>
                        <>
                            <title>{t('news')}</title>
                            <NewsHandler />
                        </>
                    </RequireAuth>
                }
            />
            <Route
                path='/news/draft'
                element={
                    <RequireAuth roles={[Role.cald, Role.trustee, Role.staff]}>
                        <>
                            <title>{t('news') + ' - ' + t('draft')}</title>
                            <NewsDraftHandler />
                        </>
                    </RequireAuth>
                }
            />
            <Route
                path='/calendar'
                element={
                    <RequireAuth roles={[Role.cald, Role.trustee, Role.staff, Role.parent]}>
                        <>
                            <title>{t('calendar')}</title>
                            <PlanningHandler />
                        </>
                    </RequireAuth>
                }
            />
            <Route
                path='/request'
                element={
                    <RequireAuth roles={[Role.cald, Role.trustee, Role.staff]}>
                        <>
                            <title>{t('regularRequestWaiting')}</title>
                            <Request />
                        </>
                    </RequireAuth>
                }
            />
            <Route
                path='/settings'
                element={
                    <RequireAuth roles={[Role.cald]}>
                        <>
                            <title>{t('settings')}</title>
                            <Settings />
                        </>
                    </RequireAuth>
                }
            />
            <Route
                path='/staff'
                element={
                    <RequireAuth roles={[Role.cald, Role.trustee, Role.staff]}>
                        <>
                            <title>{t('staff')}</title>
                            <StaffPage />
                        </>
                    </RequireAuth>
                }
            />
            <Route
                path='/drafting'
                element={
                    <RequireAuth roles={[Role.cald, Role.trustee, Role.staff]}>
                        <>
                            <title>{t('drafting')}</title>
                            <DraftingPage />
                        </>
                    </RequireAuth>
                }
            />
            <Route
                path='/suggest'
                element={
                    <RequireAuth roles={[Role.cald, Role.trustee, Role.staff]}>
                        <>
                            <title>{t('suggestions')}</title>
                            <SuggestPage />
                        </>
                    </RequireAuth>
                }
            />
            <Route
                path='/taskNote'
                element={
                    <RequireAuth roles={[Role.cald, Role.trustee, Role.staff]}>
                        <>
                            <title>{t('tasksAndNotes')}</title>
                            <TaskNotePage />
                        </>
                    </RequireAuth>
                }
            />
            <Route
                path='/meal'
                element={
                    <RequireAuth roles={[Role.cald, Role.trustee, Role.staff]}>
                        <>
                            <title>{t('meal')}</title>
                            <MealPage />
                        </>
                    </RequireAuth>
                }
            />
            {/*	 OTHER ROUTES */}
            <Route path='*' element={<Navigate to='/' replace />} />
        </Route>
    )
}
